import { v4 } from 'uuid'
import { EOneTimeLinkSentStatus } from '@/helpers/enums'

export default function ({ $auth, $axios, app, store }, inject) {
  let refreshTimer = null
  let isRefreshing = false // ✅ Prevent parallel refreshes
  let lastRefreshTime = 0 // ✅ Prevent too-frequent refreshes

  $auth.options.redirect = {
    login: '/app/login',
    logout: '/app/login',
    home: '/app/project',
    callback: '/app/login'
  }

  async function triggerTokenChecker() {
    const expires = $auth.$storage.getUniversal('_token_expiration.local')
    await $auth.initRefreshInterval(expires)
  }

  if (process.client) {
    $auth.$storage.watchState('loggedIn', (loggedIn) => {
      if (!loggedIn) {
        clearTimeout(refreshTimer)
        $auth.$storage.removeUniversal('activationPaused')
        store.dispatch('application/clearSavedApplication')
      }

      if (loggedIn) {
        triggerTokenChecker()
        app.$api.freelancer.setFreelancerLocale({ locale: app.$i18n.locale })
      }
    })
  }

  $axios.onRequest(() => {
    if (!refreshTimer && !isRefreshing) triggerTokenChecker()
  })

  $auth.initRefreshInterval = (accessTokenExp) => {
    const currentDateTime = Date.now()
    let refreshPeriod = accessTokenExp - currentDateTime - 5 * 60 * 1000 // 5 mins early

    // Skip refresh if called too soon
    if (Date.now() - lastRefreshTime < 30 * 1000) {
      console.debug('[Auth] Skipping refresh — too soon since last one.')
      return
    }

    // Handle bad or expired timestamps gracefully
    if (!accessTokenExp || isNaN(accessTokenExp)) {
      console.warn('[Auth] Invalid expiration timestamp — aborting refresh check.')
      return
    }

    // System time ahead or expired -> retry later, not immediately
    if (refreshPeriod < 0) {
      console.warn('[Auth] Refresh period negative. Adjusting refresh schedule by 5 mins.')
      refreshPeriod = 5 * 60 * 1000 // retry in 5 minutes
    }

    if (process.client) {
      // Avoid stacking multiple timers
      if (refreshTimer) {
        clearTimeout(refreshTimer)
        refreshTimer = null
      }

      // Prevent scheduling if a refresh is already in progress
      if (isRefreshing) return

      console.debug(`[Auth] Scheduling next token refresh in ${Math.round(refreshPeriod / 1000)}s`)

      refreshTimer = setTimeout(async () => {
        if (isRefreshing) return
        isRefreshing = true
        try {
          await $auth.refreshTokens()
          lastRefreshTime = Date.now()
          console.info('[Auth] Token successfully refreshed')
        } catch (err) {
          console.error('[Auth] Error refreshing token:', err)
        } finally {
          isRefreshing = false
          triggerTokenChecker()
        }
      }, refreshPeriod)
    }
  }

  inject('getBrowserUUID', async () => {
    const cookieId = '_brws_id'
    let uuid = app.$auth.$storage.getCookie(cookieId)

    if (!uuid) {
      const expires = app.$useConfig(app.$EClientConfig.USER_DEVICE_VALIDITY)
      uuid = $auth.$storage.setCookie(cookieId, v4(), { expires, httpOnly: false })
    }

    const { data: isUserDeviceTrusted } = await app.$api.freelancer.isUserDeviceTrusted(uuid)

    if (!isUserDeviceTrusted.isTrusted) {
      await app.$api.freelancer.saveUserDevice(uuid, {
        userAgent: window.navigator.userAgent,
        country: store.state.sessionCountry
      })
    }

    return uuid
  })

  inject('hasTrustedToken', () => Boolean(app.$auth.$storage.getCookie('_brws_id')))

  inject('trustedToken', () => app.$auth.$storage.getCookie('_brws_id'))

  inject('authorizeUser', async ({ token, endpoint, isOtl }) => {
    try {
      const { data } = await app.$api.login[endpoint]({
        urlToken: token,
        actorDeviceUUID: app.$trustedToken()
      })

      await app.$auth.setUserToken(data.access_token, data.refresh_token)
      return { login: 'success' }
    } catch ({ response }) {
      let errorType = null
      if (isOtl) {
        errorType =
          response?.status === 400 ? EOneTimeLinkSentStatus.EXPIRED : EOneTimeLinkSentStatus.INVALID
      }
      return { login: 'error', errorType }
    }
  })
}
